import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShareDialog } from "@/components/common";
import { Plus, Trash2, QrCode, Flag, Wand2 } from "lucide-react";

export default function ManageReferees({ data, refresh }) {
  const { tournament: t, referees, matches } = data;
  const [name, setName] = useState("");
  const [share, setShare] = useState(null);
  const origin = window.location.origin;

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(`/tournaments/${t.id}/referees`, { name }); setName(""); toast.success("Referee added"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/referees/${id}`); toast.success("Referee removed"); refresh(); };
  const distribute = async () => {
    try { const r = await api.post(`/tournaments/${t.id}/referees/distribute`); toast.success(`Assigned across ${r.data.assigned} matches`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const countFor = (id) => matches.filter((m) => m.referee_id === id).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Referees</h2>
          <p className="text-sm text-muted-foreground">{referees.length} referees</p>
        </div>
        <Button variant="outline" onClick={distribute} disabled={!referees.length || !matches.length} data-testid="distribute-referees">
          <Wand2 className="mr-1.5 h-4 w-4" /> Auto-assign to matches
        </Button>
      </div>

      <Card className="flex gap-2 p-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Referee name" data-testid="referee-name-input" />
        <Button onClick={add} data-testid="add-referee-button"><Plus className="mr-1.5 h-4 w-4" /> Add</Button>
      </Card>

      {referees.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Flag className="h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">No referees yet.</p>
        </Card>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {referees.map((r) => (
            <Card key={r.id} className="flex items-center gap-3 p-3" data-testid={`referee-item-${r.id}`}>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><Flag className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1"><div className="truncate font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{countFor(r.id)} matches assigned</div></div>
              <Button variant="ghost" size="icon" onClick={() => setShare(r)} data-testid={`share-referee-${r.id}`}><QrCode className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-referee-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </Card>
          ))}
        </div>
      )}

      {share && (
        <ShareDialog open={!!share} onOpenChange={() => setShare(null)} title={`Referee: ${share.name}`}
          subtitle="Opens the referee's mobile match view." url={`${origin}/r/${share.code}`} code={share.code} />
      )}
    </div>
  );
}
